'use client';

import { useLayoutEffect, useRef, useState } from 'react';

import cn from '@/helpers/utils/classNames';
import { styled } from '@mui/material/styles';
import MuiTooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { Theme } from '@mui/system';

interface MuiTooltipProps extends TooltipProps {
  maxWidth?: string;
  minWidth?: string;
  wrapperClassName?: string;
  variant?: 'primary' | 'error' | 'light' | 'primary-light';
  mainContainerClassName?: string;
  innerClassName?: string;
  tooltipOuterClassName?: string;
  autoHandlePosition?: boolean;
}
interface IColorScheme {
  bgColor: string;
  textColor: string;
}

const getColorScheme = (theme: Theme, variant?: 'primary' | 'light' | 'error' | 'primary-light'): IColorScheme => {
  switch (variant) {
    case 'primary':
      return {
        bgColor: '#154734',
        textColor: '#fff',
      };
    case 'primary-light':
      return {
        bgColor: '#F3F6F5',
        textColor: '#101828',
      };
    case 'error':
      return {
        bgColor: '#F04438',
        textColor: '#fff',
      };
    case 'light':
      return {
        bgColor: '#fff',
        textColor: '#101828',
      };
    default:
      return {
        bgColor: theme.palette.common.black,
        textColor: '#fff',
      };
  }
};

const Tooltip = styled(
  ({
    tooltipClassName,
    className,
    children,
    maxWidth = 'auto',
    minWidth = 'auto',
    wrapperClassName,
    mainContainerClassName,
    variant = 'primary',
    arrow = true,
    autoHandlePosition,
    innerClassName,
    tooltipOuterClassName,
    ...props
  }: MuiTooltipProps & { tooltipClassName?: string }) => {
    const parentRef = useRef<HTMLDivElement | null>(null);
    const [isParentLoaded, setIsParentLoaded] = useState(false);

    useLayoutEffect(() => {
      if (parentRef.current) {
        setIsParentLoaded(true);
      }
    }, [parentRef]);

    return (
      <div ref={parentRef} className={cn('relative', mainContainerClassName)}>
        {isParentLoaded && (
          <MuiTooltip
            className={tooltipClassName}
            {...props}
            arrow={arrow}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            PopperProps={{
              container: parentRef.current,
              className: cn('w-max', tooltipOuterClassName),
              modifiers: autoHandlePosition
                ? [
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top', 'left', 'right', 'bottom'],
                      },
                    },
                  ]
                : [],
            }}
            classes={{ popper: className, tooltip: cn(innerClassName) }}
          >
            <span className={cn('block', wrapperClassName)}>{children}</span>
          </MuiTooltip>
        )}
      </div>
    );
  }
)(({ theme, maxWidth, minWidth, variant }) => {
  const { bgColor, textColor } = getColorScheme(theme, variant);
  return {
    [`& .${tooltipClasses.arrow}`]: {
      color: bgColor,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: bgColor,
      boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      color: textColor,
      padding: '8px 12px',
      borderRadius: '8px',
      maxWidth: maxWidth,
      minWidth: minWidth,
    },
  };
});

export default Tooltip;
